import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { UploadService } from 'src/app/private/services/upload.service';
import { LocalStorageService } from '../LocalStorageService';
import { OpdchartService } from 'src/app/private/services/opdchart.service';

@Injectable({
  providedIn: 'root',
})
export class PrescriptionService {
  constructor(
    private api: OpdchartService,
    private http: HttpClient,
    private uploadService: UploadService,
    private localStorage: LocalStorageService,
  ) {
    pdfMake.vfs = (pdfFonts as any).vfs;
  }

  async generatePdf(data: any, action: any = 'print') {
    let medicationRows: any[] = [];
    let medname: any = [];
    let dosage: any = [];
    let duration: any = [];
    let qty: any = [];
    let investigations: any = [];
    let primaryCheckups: any = [];
    let advices: any = [];
    let followUp: any = '';
    let ophthalmic_examination = data?.prescription?.ophthalmic_examinations;

    const doctorObj = data?.patientInfo?.appointment?.user;
    const docPrefix = doctorObj?.prefix || 'Dr';
    const doctorName = doctorObj && (doctorObj.fname || doctorObj.lname)
      ? `${docPrefix}. ${doctorObj.fname || ''} ${doctorObj.lname || ''}`.trim()
      : (localStorage.getItem('presc_doc_name') || localStorage.getItem('profile_name') || '');

    // Load prescription template settings from local storage
    let headerConfig: any = {};
    let watermarkConfig: any = null;
    let footerConfig: any = null;

    // Robust truthy check — handles 'true', '1', 'yes' from server or UI
    const isStoredTrue = (key: string) => {
      const v = localStorage.getItem(key);
      return v === 'true' || v === '1' || v === 'yes';
    };

    // ── Debug: log key settings at PDF generation time ──
    console.log('[PrescriptionService] generatePdf called. Key settings:', {
      enablePrintLayout: localStorage.getItem('enablePrintLayout'),
      headerType: localStorage.getItem('prescriptionHeaderType'),
      clinicName: localStorage.getItem('presc_clinic_name'),
      enableLogo: localStorage.getItem('enablePrescriptionLogo'),
      enableFooter: localStorage.getItem('enablePrescriptionFooter'),
    });

    if (isStoredTrue('enablePrintLayout')) {
      const headerType =
        localStorage.getItem('prescriptionHeaderType') || 'text1';

      // ── Dynamic accent colour — set by each template, defaults to brand navy ──
      const accentColor = localStorage.getItem('prescriptionAccentColor') || '#0d4259';

      let logoImg: any = null;
      if (isStoredTrue('enablePrescriptionLogo')) {
        logoImg =
          localStorage.getItem('prescriptionLogoBase64') ||
          localStorage.getItem('getBase64logo') ||
          null;
      }

      if (headerType === 'image') {
        const headerImg = localStorage.getItem('getBase64HeaderPrescription');
        if (headerImg) {
          headerConfig = {
            columns: [
              [
                {
                  image: headerImg,
                  width: 520,
                  height: 100,
                  alignment: 'center',
                },
              ],
            ],
            margin: [40, 15, 40, 0],
          };
        }
      } else if (headerType === 'text1') {
        const clinicName =
          localStorage.getItem('presc_clinic_name') ||
          localStorage.getItem('hospital_name') ||
          '';
        const clinicAddr =
          localStorage.getItem('presc_clinic_address') ||
          localStorage.getItem('hospital_address') ||
          '';
        const clinicPhone = localStorage.getItem('presc_clinic_phone') || '';
        const clinicEmail = localStorage.getItem('profile_email') || '';
        const doctorObj = data?.patientInfo?.appointment?.user;
        const docPrefix = doctorObj?.prefix || 'Dr';
        const doctorName = doctorObj && (doctorObj.fname || doctorObj.lname)
          ? `${docPrefix}. ${doctorObj.fname || ''} ${doctorObj.lname || ''}`.trim()
          : (localStorage.getItem('presc_doc_name') || localStorage.getItem('profile_name') || '');
        const doctorDegree = doctorObj?.profile || localStorage.getItem('presc_doc_degree') || '';
        const doctorReg = localStorage.getItem('presc_doc_reg') || '';

        if (logoImg) {
          headerConfig = {
            stack: [
              {
                columns: [
                  // Left side: Logo + Clinic Info
                  {
                    columns: [
                      {
                        image: logoImg,
                        fit: [70, 70],
                        width: 75,
                        margin: [0, 2, 10, 2],
                      },
                      {
                        stack: [
                          {
                            text: clinicName,
                            fontSize: 13,
                            bold: true,
                            color: accentColor,
                          },
                          ...(clinicAddr
                            ? [
                                {
                                  text: clinicAddr,
                                  fontSize: 8,
                                  color: '#64748b',
                                  margin: [0, 2, 0, 0],
                                },
                              ]
                            : []),
                          ...(clinicPhone
                            ? [
                                {
                                  text: clinicPhone,
                                  fontSize: 8,
                                  color: '#475569',
                                  margin: [0, 2, 0, 0],
                                },
                              ]
                            : []),
                        ],
                        width: '*',
                      },
                    ],
                    width: '*',
                  },
                  // Right side: Clinic Email
                  {
                    stack: [
                      ...(clinicEmail
                        ? [
                            {
                              text: clinicEmail,
                              fontSize: 8,
                              color: '#64748b',
                              alignment: 'right',
                              margin: [0, 2, 0, 0],
                            },
                          ]
                        : []),
                    ],
                    width: 'auto',
                  },
                ],
                columnGap: 16,
              },
              {
                canvas: [
                  { type: 'rect', x: 0, y: 8, w: 515, h: 1, color: '#cbd5e1' },
                ],
              },
            ],
            margin: [40, 20, 40, 6],
          };
        } else {
          headerConfig = {
            stack: [
              {
                columns: [
                  // Left side: Clinic Info
                  {
                    stack: [
                      {
                        text: clinicName,
                        fontSize: 13,
                        bold: true,
                        color: accentColor,
                      },
                      ...(clinicAddr
                        ? [
                            {
                              text: clinicAddr,
                              fontSize: 8,
                              color: '#64748b',
                              margin: [0, 2, 0, 0],
                            },
                          ]
                        : []),
                      ...(clinicPhone
                        ? [
                            {
                              text: clinicPhone,
                              fontSize: 8,
                              color: '#475569',
                              margin: [0, 2, 0, 0],
                            },
                          ]
                        : []),
                    ],
                    width: '*',
                  },
                  // Right side: Clinic Email
                  {
                    stack: [
                      ...(clinicEmail
                        ? [
                            {
                              text: clinicEmail,
                              fontSize: 8,
                              color: '#64748b',
                              alignment: 'right',
                              margin: [0, 2, 0, 0],
                            },
                          ]
                        : []),
                    ],
                    width: 'auto',
                  },
                ],
                columnGap: 16,
              },
              {
                canvas: [
                  { type: 'rect', x: 0, y: 8, w: 515, h: 1, color: '#cbd5e1' },
                ],
              },
            ],
            margin: [40, 20, 40, 6],
          };
        }
      } else if (headerType === 'text2') {
        // ── Modern Banner: compact logo | vertical line | clinic bold info | bottom accent ──
        const clinicName =
          localStorage.getItem('presc_clinic_name') ||
          localStorage.getItem('hospital_name') ||
          '';
        const clinicAddr =
          localStorage.getItem('presc_clinic_address') ||
          localStorage.getItem('hospital_address') ||
          '';
        const clinicPhone = localStorage.getItem('presc_clinic_phone') || '';
        const clinicEmail = localStorage.getItem('profile_email') || '';
        const doctorObj = data?.patientInfo?.appointment?.user;
        const docPrefix = doctorObj?.prefix || 'Dr';
        const doctorName = doctorObj && (doctorObj.fname || doctorObj.lname)
          ? `${docPrefix}. ${doctorObj.fname || ''} ${doctorObj.lname || ''}`.trim()
          : (localStorage.getItem('presc_doc_name') || localStorage.getItem('profile_name') || '');
        const doctorDegree = doctorObj?.profile || localStorage.getItem('presc_doc_degree') || '';
        const doctorReg = localStorage.getItem('presc_doc_reg') || '';

        if (logoImg) {
          headerConfig = {
            stack: [
              {
                columns: [
                  // Column 1: Logo — small, compact, vertically centred
                  {
                    image: logoImg,
                    fit: [50, 50],
                    width: 60,
                    alignment: 'center',
                    margin: [0, 4, 0, 4],
                  },
                  // Column 2: Thin vertical rule drawn with canvas line
                  {
                    canvas: [
                      {
                        type: 'line',
                        x1: 4,
                        y1: 0,
                        x2: 4,
                        y2: 65,
                        lineWidth: 1.5,
                        lineColor: accentColor,
                      },
                    ],
                    width: 14,
                  },
                  // Column 3: Clinic info and Doctor details side by side
                  {
                    columns: [
                      // Left: Clinic details
                      {
                        stack: [
                          {
                            text: clinicName.toUpperCase(),
                            fontSize: 14,
                            bold: true,
                            color: accentColor,
                            margin: [0, 0, 0, 5],
                          },
                          ...(clinicAddr
                            ? [
                                {
                                  text: clinicAddr,
                                  fontSize: 8,
                                  color: '#475569',
                                  margin: [0, 0, 0, 2],
                                },
                              ]
                            : []),
                          ...(clinicPhone
                            ? [
                                {
                                  text: clinicPhone,
                                  fontSize: 8,
                                  color: '#475569',
                                },
                              ]
                            : []),
                        ],
                        width: '*',
                        margin: [8, 6, 0, 4],
                      },
                      // Right: Clinic Email
                      {
                        stack: [
                          ...(clinicEmail
                            ? [
                                {
                                  text: clinicEmail,
                                  fontSize: 8,
                                  color: '#64748b',
                                  alignment: 'right',
                                  margin: [0, 2, 0, 0],
                                },
                              ]
                            : []),
                        ],
                        width: 'auto',
                        margin: [0, 6, 0, 4],
                      },
                    ],
                    width: '*',
                  },
                ],
                columnGap: 0,
              },
              // Solid thick bottom accent line
              {
                canvas: [
                  { type: 'rect', x: 0, y: 4, w: 515, h: 3, color: accentColor },
                ],
              },
            ],
            margin: [40, 16, 40, 6],
          };
        } else {
          // No logo — clinic name left | vertical line | contact right
          headerConfig = {
            stack: [
              {
                columns: [
                  {
                    stack: [
                      {
                        text: clinicName.toUpperCase(),
                        fontSize: 14,
                        bold: true,
                        color: accentColor,
                      },
                    ],
                    width: 150,
                    margin: [0, 8, 0, 8],
                  },
                  {
                    canvas: [
                      {
                        type: 'line',
                        x1: 4,
                        y1: 0,
                        x2: 4,
                        y2: 55,
                        lineWidth: 1.5,
                        lineColor: accentColor,
                      },
                    ],
                    width: 14,
                  },
                  {
                    columns: [
                      // Left: Clinic Details
                      {
                        stack: [
                          ...(clinicAddr
                            ? [
                                {
                                  text: clinicAddr,
                                  fontSize: 8,
                                  color: '#475569',
                                  margin: [0, 0, 0, 2],
                                },
                              ]
                            : []),
                          ...(clinicPhone
                            ? [
                                {
                                  text: clinicPhone,
                                  fontSize: 8,
                                  color: '#475569',
                                  margin: [0, 0, 0, 2],
                                },
                              ]
                            : []),
                        ],
                        width: '*',
                        margin: [8, 6, 0, 4],
                      },
                      // Right: Clinic Email
                      {
                        stack: [
                          ...(clinicEmail
                            ? [
                                {
                                  text: clinicEmail,
                                  fontSize: 8,
                                  color: '#64748b',
                                  alignment: 'right',
                                  margin: [0, 2, 0, 0],
                                },
                              ]
                            : []),
                        ],
                        width: 'auto',
                        margin: [0, 6, 0, 4],
                      },
                    ],
                    width: '*',
                  },
                ],
                columnGap: 0,
              },
              {
                canvas: [
                  { type: 'rect', x: 0, y: 4, w: 515, h: 3, color: accentColor },
                ],
              },
            ],
            margin: [40, 16, 40, 6],
          };
        }
      }
    }

    if (isStoredTrue('enablePrescriptionWatermark')) {
      const wmText =
        localStorage.getItem('prescriptionWatermarkText') || 'EMR PRESCRIPTION';
      const wmOpacity = parseFloat(
        localStorage.getItem('prescriptionWatermarkOpacity') || '0.08',
      );
      watermarkConfig = {
        text: wmText,
        color: 'blue',
        opacity: wmOpacity,
        bold: true,
        italics: false,
      };
    }

    if (isStoredTrue('enablePrintLayout')) {
      if (isStoredTrue('enablePrescriptionFooter')) {
        const footerText = localStorage.getItem('prescriptionFooterText') || '';
        footerConfig = function (currentPage: number, pageCount: number) {
          return {
            stack: [
              {
                canvas: [
                  { type: 'rect', x: 40, y: 0, w: 515, h: 1, color: '#cbd5e1' },
                ],
              },
              {
                columns: [
                  {
                    text: footerText,
                    alignment: 'left',
                    fontSize: 8,
                    color: '#64748b',
                    margin: [40, 8, 0, 0],
                  },
                  {
                    text: `Page ${currentPage} of ${pageCount}`,
                    alignment: 'right',
                    fontSize: 8,
                    color: '#94a3b8',
                    margin: [0, 8, 40, 0],
                  },
                ],
              },
            ],
          };
        };
      } else {
        const footerImg = localStorage.getItem('getBase64FooterPrescription');
        if (footerImg) {
          footerConfig = {
            columns: [
              [
                {
                  image: footerImg,
                  width: 600,
                  alignment: 'center',
                },
              ],
            ],
          };
        }
      }
    }

    if (data?.prescription?.primary_checkups != null) {
      Object.entries(data?.prescription?.primary_checkups).forEach((entry) => {
        const [key, value] = entry;
        let str = key.replaceAll('_', ' ');
        let name = str.charAt(0).toUpperCase() + str.slice(1);
        if (value != '') {
          primaryCheckups.push({
            margin: [0, 2, 0, 2],
            text: [{ text: name + ' : ' + value, fontSize: 10, style: '' }],
          });
        }
      });
    }

    if (data?.prescription?.advices != null) {
      followUp = data?.prescription?.advices?.nextVisit;
      data?.prescription?.advices.advices.forEach(
        (element: any, index: any) => {
          let i = index + 1;
          advices.push({ margin: [0, 2, 0, 2], text: element, fontSize: 10 });
        },
      );
    }

    if (data?.prescription?.investigations != null) {
      data?.prescription?.investigations.forEach((element: any, index: any) => {
        let i = index + 1;
        investigations.push({
          margin: [0, 2, 0, 2],
          text: element.investigation_name,
          fontSize: 10,
        });
      });
    }

    if (data?.prescription?.medications != null) {
      const filteredMeds = data.prescription.medications.filter(
        (item: any) => item.drug_type !== 'HANDWRITTEN_METADATA',
      );
      filteredMeds.forEach((element: any, index: any) => {
        let i = index + 1;

        let nameCell = {
          margin: [0, 4, 0, 4],
          text: [
            { text: i + '. ', fontSize: 10 },
            { text: element.drug_name.medicine_name, fontSize: 10, bold: true },
            element.drug_name.composition
              ? {
                  text: '\n' + element.drug_name.composition,
                  fontSize: 8,
                  color: '#555555',
                }
              : { text: '' },
          ],
        };

        let doseText = '';
        if (element.dosage) {
          if (Array.isArray(element.dosage)) {
            element.dosage.forEach((d: any, idx: any) => {
              if (idx > 0) {
                doseText += ', ';
              }
              doseText += d;
            });
          } else {
            doseText = String(element.dosage);
          }
        } else {
          doseText =
            `${element.duration_count || ''} ${element.duration || ''} ${element.frequency || ''} ${element.quantity || ''}`.trim();
        }

        if (!doseText) {
          doseText = 'As directed';
        }

        let dosageCell = {
          margin: [0, 4, 0, 4],
          text: '  ' + doseText,
          alignment: 'left',
          fontSize: 10,
        };

        medicationRows.push([nameCell, dosageCell]);
      });
    }
    let docDefinition: any = {
      watermark: watermarkConfig,
      pageMargins:
        isStoredTrue('enablePrintLayout')
          ? [40, 145, 40, 55]
          : [40, 40, 40, 40],
      header: headerConfig,
      footer: footerConfig,
      content: [
        {
          margin: [0, 15, 0, 5],
          columns: [
            [
              {
                text: data?.patientInfo.pId,
                alignment: 'left',
                bold: true,
                fontSize: 10,
              },
              {
                text:
                  data?.patientInfo.fname +
                  ' ' +
                  data?.patientInfo.lname +
                  ' ' +
                  '(' +
                  data?.patientInfo.gender +
                  ') / ' +
                  ' ' +
                  data?.patientInfo.age +
                  'Y',

                fontSize: 11,
              },
              { text: data?.patientInfo.email, fontSize: 10 },
              { text: data?.patientInfo.mobile, fontSize: 10 },
              { text: data?.patientInfo.address, fontSize: 10 },

              // { margin: [0, 5,0, 5]  , text: "Weight(KG):70  | Height(cms):165 | BP:120/20 mmHg",fontSize: 10,  bold:true },
            ],
            [
              {
                text: 'Doctor Name: ' + doctorName,
                alignment: 'right',
                fontSize: 10,
                bold: true,
              },
              {
                text: `Date: ${new Date().toLocaleString()}`,
                alignment: 'right',
                fontSize: 10,
                bold: true,
              },
            ],
          ],
        },
      ],
      styles: {
        sectionHeader: {
          bold: true,
          decoration: 'underline',
          fontSize: 14,
          margin: [0, 5, 0, 5],
        },
        ul: {
          fontSize: 10,
        },
        tableHeader: {
          bold: true,
          fontSize: 11,
          color: 'black',
          border: [false, true, false, true],
        },
      },
    };

    //Primary Examinations
    if (
      data.print_option.includes('primary_checkups') &&
      data?.prescription?.primary_checkups != null
    ) {
      docDefinition.content.push(
        {
          text: 'Primary Checkups',
          style: 'sectionHeader',
          fontSize: 11,
        },
        {
          fontSize: 10,
          ol: primaryCheckups,
        },
      );
    }

    //Auto Refraction

    //Auto Refraction Table
    if (
      data.print_option.includes('auto_refraction') &&
      ophthalmic_examination != null
    ) {
      docDefinition.content.push({
        margin: [0, 5, 0, 5],

        columns: [
          [
            {
              text: 'Auto Refraction',
              style: 'sectionHeader',
              fontSize: 11,
            },
            {
              table: {
                body: [
                  [
                    {},
                    {
                      text: 'Right Eye',
                      fontSize: 10,
                      style: 'tableHeader',
                      colSpan: 3,
                      alignment: 'center',
                    },
                    {},
                    {},

                    {
                      text: 'Left Eye',
                      fontSize: 10,
                      style: 'tableHeader',
                      colSpan: 3,
                      alignment: 'center',
                    },
                    {},
                    {},
                  ],
                  [
                    '',
                    { text: 'Sph', fontSize: 10, alignment: 'center' },
                    { text: 'Cyl', fontSize: 10, alignment: 'center' },
                    { text: 'Axis', fontSize: 10, alignment: 'center' },
                    { text: 'Sph', fontSize: 10, alignment: 'center' },
                    { text: 'Cyl', fontSize: 10, alignment: 'center' },
                    { text: 'Axis', fontSize: 10, alignment: 'center' },
                  ],
                  [
                    { text: 'Undilated', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.auto_refraction.right
                        .undilated.spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.right
                        .undilated.cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.right
                        .undilated.axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left
                        .undilated.spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left
                        .undilated.cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left
                        .undilated.axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                  [
                    { text: 'Dilated', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.auto_refraction.right.dilated
                        .spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.right.dilated
                        .cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.right.dilated
                        .axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left.dilated
                        .spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left.dilated
                        .cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left.dilated
                        .axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                ],
              },
            },
          ],
          [
            {
              text: 'Auto Refraction',
              style: 'sectionHeader',
              fontSize: 11,
            },

            {
              table: {
                body: [
                  [
                    {},
                    {
                      text: 'Right Eye',
                      fontSize: 10,
                      style: 'tableHeader',
                      colSpan: 3,
                      alignment: 'center',
                    },
                    {},
                    {},

                    {
                      text: 'Left Eye',
                      fontSize: 10,
                      style: 'tableHeader',
                      colSpan: 3,
                      alignment: 'center',
                    },
                    {},
                    {},
                  ],
                  [
                    '',
                    { text: 'Sph', fontSize: 10, alignment: 'center' },
                    { text: 'Cyl', fontSize: 10, alignment: 'center' },
                    { text: 'Axis', fontSize: 10, alignment: 'center' },
                    { text: 'Sph', fontSize: 10, alignment: 'center' },
                    { text: 'Cyl', fontSize: 10, alignment: 'center' },
                    { text: 'Axis', fontSize: 10, alignment: 'center' },
                  ],

                  [
                    { text: 'Subjective	', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.auto_refraction.right
                        .subjective.spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.right
                        .subjective.cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.right
                        .subjective.axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left
                        .subjective.spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left
                        .subjective.cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left
                        .subjective.axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                  [
                    { text: 'Cycloplegic	', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.auto_refraction.right
                        .cycloplegic.spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.right
                        .cycloplegic.cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.right
                        .cycloplegic.axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left
                        .cycloplegic.spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left
                        .cycloplegic.cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.auto_refraction.left
                        .cycloplegic.axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                ],
              },
            },
          ],
        ],
      });
    }

    //Vision Acuity

    if (
      data.print_option.includes('vision_acuity') &&
      ophthalmic_examination != null
    ) {
      docDefinition.content.push({
        margin: [0, 5, 0, 5],

        columns: [
          [
            {
              text: 'Vision Acuity',
              style: 'sectionHeader',
              fontSize: 11,
            },
            {
              table: {
                body: [
                  [
                    { text: '', fontSize: 10, alignment: 'center', bold: true },
                    {
                      text: 'RE',
                      fontSize: 10,
                      alignment: 'center',
                      bold: true,
                    },
                    {
                      text: 'LE',
                      fontSize: 10,
                      alignment: 'center',
                      bold: true,
                    },
                    {
                      text: 'RE',
                      fontSize: 10,
                      alignment: 'center',
                      bold: true,
                    },
                    {
                      text: 'LE',
                      fontSize: 10,
                      alignment: 'center',
                      bold: true,
                    },
                  ],
                  [
                    { text: 'Unaided', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.vision_acuity.right.dist
                        .unaided,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.left.dist
                        .unaided,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.right.near
                        .unaided,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.left.near
                        .unaided,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                  [
                    { text: 'With Glass', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.vision_acuity.right.dist
                        .with_glass,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.left.dist
                        .with_glass,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.right.near
                        .with_glass,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.left.near
                        .with_glass,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],

                  [
                    { text: 'With Pinhole', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.vision_acuity.right.dist
                        .with_pinhole,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.left.dist
                        .with_pinhole,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.right.near
                        .with_pinhole,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.left.near
                        .with_pinhole,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                  [
                    { text: 'PGP', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.vision_acuity.right.dist.pgp,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.left.dist.pgp,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.right.near.pgp,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.vision_acuity.left.near.pgp,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                ],
              },
            },
          ],
        ],
      });
    }

    //Refraction Table
    if (
      data.print_option.includes('refraction') &&
      ophthalmic_examination != null
    ) {
      docDefinition.content.push({
        margin: [0, 5, 0, 5],

        columns: [
          [
            {
              text: 'Refraction (Glass RX)',
              style: 'sectionHeader',
              fontSize: 11,
            },
            {
              table: {
                body: [
                  [
                    {},
                    {
                      text: 'Right Eye',
                      fontSize: 10,
                      style: 'tableHeader',
                      colSpan: 4,
                      alignment: 'center',
                    },
                    {},
                    {},
                    {},

                    {
                      text: 'Left Eye',
                      fontSize: 10,
                      style: 'tableHeader',
                      colSpan: 4,
                      alignment: 'center',
                    },
                    {},
                    {},
                    {},
                  ],
                  [
                    '',
                    { text: 'Sph', fontSize: 10, alignment: 'center' },
                    { text: 'Cyl', fontSize: 10, alignment: 'center' },
                    { text: 'Axis', fontSize: 10, alignment: 'center' },
                    { text: 'Add', fontSize: 10, alignment: 'center' },
                    { text: 'Sph', fontSize: 10, alignment: 'center' },
                    { text: 'Cyl', fontSize: 10, alignment: 'center' },
                    { text: 'Axis', fontSize: 10, alignment: 'center' },
                    { text: 'Add', fontSize: 10, alignment: 'center' },
                  ],
                  [
                    { text: 'Dist', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.refraction.right.dist.spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.right.dist.cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.right.dist.axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.right.dist.add,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.left.dist.spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.left.dist.cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.left.dist.axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.left.dist.add,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                  [
                    { text: 'Near', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.refraction.right.near.spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.right.near.cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.right.near.axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.right.near.add,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.left.near.spl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.left.near.cyl,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.left.near.axis,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.refraction.left.near.add,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                ],
              },
            },
          ],
        ],
      });
    }

    //Anterior Segment
    if (
      data.print_option.includes('anterior_segment') &&
      ophthalmic_examination != null
    ) {
      docDefinition.content.push({
        margin: [0, 10, 0, 5],

        columns: [
          [
            {
              text: 'Anterior Segment',
              style: 'sectionHeader',
              fontSize: 11,
            },
            {
              table: {
                body: [
                  [
                    {
                      text: '',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Lids & Adnexa',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Conjucative',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Anterior Chamber',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Cornea',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Iris',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Pupil',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Lens',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                  ],
                  [
                    { text: 'RE', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.anterior_segment.right
                        .lids_and_adnexa,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.right
                        .conjucative,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.right
                        .anterior_chamber,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.right
                        .cornea,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.right.iris,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.right.pupil,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.right.lens,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                  [
                    { text: 'LE', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.anterior_segment.left
                        .lids_and_adnexa,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.left
                        .conjucative,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.left
                        .anterior_chamber,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.left.cornea,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.left.iris,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.left.pupil,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.anterior_segment.left.lens,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                ],
              },
            },
          ],
        ],
      });
    }

    //Fundus Examinations

    if (
      data.print_option.includes('fundus_examination') &&
      ophthalmic_examination != null
    ) {
      docDefinition.content.push({
        margin: [0, 10, 0, 5],

        columns: [
          [
            {
              text: 'Fundus Examinations',
              style: 'sectionHeader',
              fontSize: 11,
            },
            {
              table: {
                body: [
                  [
                    {
                      text: '',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Cup to Disc',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Vitreous',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Optic Nerve',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Macula',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Vassals',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Periphery',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                  ],
                  [
                    { text: 'RE', fontSize: 10, alignment: 'center' },

                    {
                      text: ophthalmic_examination.fundus_examination.right
                        .cup_to_disc,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.fundus_examination.right
                        .vitreous,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.fundus_examination.right
                        .optic_nerve,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.fundus_examination.right
                        .macula,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.fundus_examination.right
                        .vassals,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.fundus_examination.right
                        .periphery,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                  [
                    { text: 'LE', fontSize: 10, alignment: 'center' },

                    {
                      text: ophthalmic_examination.fundus_examination.left
                        .cup_to_disc,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.fundus_examination.left
                        .vitreous,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.fundus_examination.left
                        .optic_nerve,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.fundus_examination.left
                        .macula,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.fundus_examination.left
                        .vassals,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.fundus_examination.left
                        .periphery,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                ],
              },
            },
          ],
        ],
      });
    }

    //IOP
    if (data.print_option.includes('iop') && ophthalmic_examination != null) {
      docDefinition.content.push({
        margin: [0, 10, 0, 5],

        columns: [
          [
            {
              text: 'IOP',
              style: 'sectionHeader',
              fontSize: 11,
            },
            {
              table: {
                body: [
                  [
                    {
                      text: '',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'Method of IOP	',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'IOP Time	',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'IOP',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'MM OF HG',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                    {
                      text: 'CCT',
                      fontSize: 10,
                      style: 'tableHeader',
                      alignment: 'center',
                    },
                  ],
                  [
                    { text: 'RE', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.iop.right.method,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.iop.right.iop_time,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.iop.right.iop,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.iop.right.mm_of_hg,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.iop.right.cct,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                  [
                    { text: 'LE', fontSize: 10, alignment: 'center' },
                    {
                      text: ophthalmic_examination.iop.left.method,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.iop.left.iop_time,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.iop.left.iop,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.iop.left.mm_of_hg,
                      fontSize: 10,
                      alignment: 'center',
                    },
                    {
                      text: ophthalmic_examination.iop.left.cct,
                      fontSize: 10,
                      alignment: 'center',
                    },
                  ],
                ],
              },
            },
          ],
        ],
      });
    }

    if (
      data.print_option.includes('investigations') &&
      data?.prescription?.investigations != null
    ) {
      docDefinition.content.push(
        {
          fontSize: 11,
          margin: [0, 5, 0, 5],
          text: 'Investigations',
          style: 'sectionHeader',
        },

        {
          margin: [0, 0, 0, 10],
          fontSize: 10,
          ol: investigations,
        },
      );
    }

    if (data?.prescription?.medications != null) {
      docDefinition.content.push(
        {
          fontSize: 11,
          margin: [0, 10, 0, 5],
          text: 'Rx',
          style: 'sectionHeader',
        },
        {
          layout: 'noBorders',
          margin: [0, 2, 0, 2],
          fontSize: 10,
          table: {
            widths: ['*', '*'],
            body: medicationRows,
          },
        },
      );
    }

    if (
      data.print_option.includes('advices') &&
      data?.prescription?.advices != null
    ) {
      docDefinition.content.push(
        {
          fontSize: 11,
          margin: [0, 5, 0, 5],
          text: 'Advice Given',
          style: 'sectionHeader',
        },

        {
          margin: [0, 0, 0, 10],
          fontSize: 10,
          ul: advices,
        },
        {
          fontSize: 11,
          margin: [0, 5, 0, 5],
          text: 'Next Visit: ' + followUp,
          style: 'sectionHeader',
        },
      );
    }

    //  {
    //   alignment: 'right',
    //   columns:[
    //     [
    //       { text:data.doctor,bold:true, fontSize: 10,},
    //       { text:'MBBS,MD,MS ',bold:true  ,fontSize: 10,}
    //     ]
    //   ],
    // }

    const docFullName = `${docPrefix}. ${doctorObj?.fname || ''} ${doctorObj?.lname || ''}`.trim();
    const docDegree = doctorObj?.profile || '';

    let signatureImageElement: any = null;
    if (doctorObj?.signature) {
      signatureImageElement = {
        image: doctorObj.signature,
        width: 100,
        height: 40,
        alignment: 'right',
        margin: [0, 5, 0, 5]
      };
    } else {
      signatureImageElement = {
        text: `/ ${docFullName} /`,
        italics: true,
        fontSize: 13,
        color: '#475569',
        alignment: 'right',
        margin: [0, 15, 0, 5]
      };
    }

    docDefinition.content.push({
      margin: [0, 25, 0, 0],
      keepTogether: true,
      columns: [
        { text: '', width: '*' },
        {
          stack: [
            signatureImageElement,
            {
              canvas: [
                { type: 'line', x1: 0, y1: 2, x2: 120, y2: 2, lineWidth: 1, lineColor: '#cbd5e1' }
              ]
            },
            {
              text: docFullName,
              bold: true,
              fontSize: 10,
              alignment: 'center',
              margin: [0, 4, 0, 2]
            },
            ...(docDegree ? [{
              text: docDegree,
              fontSize: 8,
              color: '#64748b',
              alignment: 'center'
            }] : [])
          ],
          width: 120,
          alignment: 'right'
        }
      ]
    });

    if (localStorage.getItem('enablePrintLayout') == 'false') {
      delete docDefinition.content.header;
      delete docDefinition.content.footer;
    }

    if (action === 'download') {
      pdfMake.createPdf(docDefinition).download();
    }
    if (action === 'email') {
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);

      const pdfData = (d: any) =>
        new Promise((resolve) => {
          d.getBuffer(async (buffer: ArrayBuffer) => {
            const res = await this.sendPdfToApi(buffer, data);
            //console.log(res)
            resolve(res);
          });
        });
      return await pdfData(pdfDocGenerator);
    } else if (action === 'view') {
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      const pdfData = (data: any) =>
        new Promise((resolve) => {
          data.getBuffer((buffer: any) => {
            const dataUrl = URL.createObjectURL(
              new Blob([buffer], {
                type: 'application/pdf',
              }),
            );
            //console.log(url)
            resolve(dataUrl);
          });
        });
      return await pdfData(pdfDocGenerator);
    } else if (action === 'print') {
      pdfMake.createPdf(docDefinition).print();
    } else {
      pdfMake.createPdf(docDefinition).open();
    }
  }
  getPdfData(buffer: ArrayBuffer, _patientData: any) {}

  async sendPdfToApi(buffer: ArrayBuffer, _patientData: any) {
    const binaryData = new Uint8Array(buffer);
    const blob = new Blob([binaryData], { type: 'application/pdf' });
    const formData = new FormData();
    let folder =
      'public/hospital-' +
      this.localStorage.getHospitalId() +
      '/' +
      _patientData.pId +
      '/prescriptions';
    formData.append('file', blob, 'prescription_' + _patientData.app_id);
    formData.append('folders', folder);
    formData.append('bucket', 'emruploads');
    let response = {};
    //console.log(formData)
    const sendPdf = () =>
      new Promise((resolve) => {
        this.uploadService.uploadPatientProfile(formData).subscribe({
          next: (res) => {
            // console.log(res)
            if (res) {
              let param = {
                hospital_id: this.localStorage.getHospitalId(),
                app_id: _patientData.app_id,
                link: res.url,
              };
              this.api.sharePrescription(param).subscribe({
                next: (res) => {
                  //console.log(res);
                  if (res) {
                    response = res;
                    resolve(res);
                  }
                },
                error: (e) => {
                  response = e;
                },
              });
            }
          },
          error: (e) => {},
        });
      });
    let d = await sendPdf();
    return d;
  }
}
